import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { useTimeContext } from "./DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Wallet, TrendingUp, BarChart3, PieChart, ArrowUpRight, ArrowDownRight, Database, Layers, Target } from "lucide-react";
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell, PieChart as RPieChart, Pie } from "recharts";
import AIPageInsight from "@/components/AIPageInsight";
import { PeriodBadge } from "@/components/Formatters";
import TargetManager from "@/components/TargetManager";
import CollectionOutlook from "@/components/CollectionOutlook";
import CRMPerformance from "@/components/CRMPerformance";
import CollectionAnalytics from "@/components/CollectionAnalytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const RECEIPT_ALIAS = { 'Sales Payment': 'Refund', 'Sales Receipt': 'CRM Collection', 'Temp Receipt': 'Sales Collection', 'Admin Receipt': 'Reg. Payment' };
const fmt = (n) => { if (n == null) return '--'; const a = Math.abs(n); if (a >= 1e7) return `${(n / 1e7).toFixed(2)} Cr`; if (a >= 1e5) return `${(n / 1e5).toFixed(2)} L`; if (a >= 1e3) return `${(n / 1e3).toFixed(1)}K`; return n.toLocaleString(); };
const COLORS = ['#1E5FAD', '#10B981', '#F59E0B', '#8B5CF6', '#EF4444', '#06B6D4', '#EC4899', '#84CC16', '#F97316', '#6366F1'];

export default function CollectionIntelligence() {
  const { period, refDate, token, currentMonthInfo } = useTimeContext();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("performance");

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const params = { period };
      if (refDate) params.ref_date = refDate;
      const res = await axios.get(`${API}/kpi/executive`, { headers: { Authorization: `Bearer ${token}` }, params });
      setData(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- API/axios are module constants, setters are stable
  }, [token, period, refDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const d = data || {};
  const collection = d.collection || 0;
  const demand = d.demand || 0;
  const collTarget = d.collection_target || 0;
  const collEfficiencyVsTarget = d.collection_efficiency || 0;
  const byProject = d.collection_by_project || [];
  const byProjectTarget = d.collection_by_project_with_target || [];
  const byType = d.collection_by_type || [];
  const trend = d.collection_trend || [];
  const hasData = collection > 0 || byProject.length > 0;

  // Derived analytics
  const collEfficiency = demand > 0 ? ((collection / demand) * 100).toFixed(1) : null;
  const avgPerProject = byProject.length > 0 ? collection / byProject.length : 0;
  const topProject = byProject[0];
  const topProjectPct = topProject && collection > 0 ? ((topProject.value / collection) * 100).toFixed(1) : 0;

  // Concentration analysis
  const top3Value = byProject.slice(0, 3).reduce((s, p) => s + p.value, 0);
  const top3Pct = collection > 0 ? ((top3Value / collection) * 100).toFixed(1) : 0;

  // Project data with % share
  const projectData = byProject.slice(0, 15).map(p => ({ ...p, pct: collection > 0 ? ((p.value / collection) * 100).toFixed(1) : 0 }));

  if (!hasData && !loading) return (
    <div className="space-y-5" data-testid="collection-intelligence-page">
      <div>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Collection Intelligence</h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Cash inflow analysis by project, receipt type, and trends</p>
      </div>
      <div className="empty-state" data-testid="collection-empty-state"><Database className="h-12 w-12 text-slate-300 mb-4" /><p className="text-base font-medium text-slate-500">No Collection Data for This Period</p><p className="text-sm text-slate-400 mt-1">Upload Cashflow Table or select a different period</p></div>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="collection-intelligence-page">
      {/* Header */}
      <div>
        <h1 className="text-xl sm:text-2xl lg:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>Collection Intelligence</h1>
        <p className="text-xs sm:text-sm text-slate-500 mt-0.5">Cash inflow analysis</p>
        <PeriodBadge period={period} currentMonthInfo={currentMonthInfo} />
      </div>

      {/* Tab Switcher */}
      <div className="flex items-center gap-1 bg-slate-100 rounded-xl p-1" data-testid="collection-tabs">
        <button onClick={() => setActiveTab("performance")} className={`flex-1 py-2.5 text-xs font-bold rounded-lg transition-all text-center ${activeTab === "performance" ? 'bg-[#1D4ED8] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-testid="tab-performance">
          Performance
        </button>
        <button onClick={() => setActiveTab("outlook")} className={`flex-1 py-2.5 text-xs font-bold rounded-lg transition-all text-center ${activeTab === "outlook" ? 'bg-[#1D4ED8] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-testid="tab-outlook">
          Outlook
        </button>
        <button onClick={() => setActiveTab("crm")} className={`flex-1 py-2.5 text-xs font-bold rounded-lg transition-all text-center ${activeTab === "crm" ? 'bg-[#1D4ED8] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-testid="tab-crm">
          CRM
        </button>
        <button onClick={() => setActiveTab("analytics")} className={`flex-1 py-2.5 text-xs font-bold rounded-lg transition-all text-center ${activeTab === "analytics" ? 'bg-[#1D4ED8] text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-testid="tab-analytics">
          Analytics
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === "outlook" ? (
        <CollectionOutlook />
      ) : activeTab === "crm" ? (
        <CRMPerformance />
      ) : activeTab === "analytics" ? (
        <CollectionAnalytics />
      ) : (
      <>

      <AIPageInsight token={token} period={period} refDate={refDate} context={`Collection: ${fmt(collection)}, Efficiency: ${collEfficiencyVsTarget}%, Target: ${fmt(collTarget)}, Top project: ${topProject?.name || 'N/A'} (${fmt(topProject?.value)}), Top 3 share: ${top3Pct}%`} pageTitle="Collection" />

      {/* KPI Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2.5 sm:gap-3">
        <div className="kpi-card" data-testid="coll-total">
          <div className="flex items-center gap-1.5 mb-1.5"><Wallet className="h-4 w-4 text-emerald-500" /><span className="text-[9px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">Collection</span></div>
          <p className="text-lg sm:text-xl font-bold text-emerald-600" style={{ fontFamily: 'Manrope' }}>{fmt(collection)}</p>
          <p className="text-[9px] text-slate-500 mt-1">{collEfficiencyVsTarget}% of target</p>
        </div>
        <div className="kpi-card" data-testid="coll-target">
          <div className="flex items-center gap-1.5 mb-1.5"><Target className="h-4 w-4 text-blue-500" /><span className="text-[9px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">Target</span></div>
          <p className="text-lg sm:text-xl font-bold text-blue-600" style={{ fontFamily: 'Manrope' }}>{fmt(collTarget)}</p>
          <p className="text-[9px] text-slate-500 mt-1">{collTarget > 0 && collection > collTarget ? <span className="text-emerald-600 font-medium">Exceeded</span> : collTarget > 0 ? <span className="text-amber-600 font-medium">Gap: {fmt(collTarget - collection)}</span> : ''}</p>
        </div>
        <div className="kpi-card" data-testid="coll-top-project">
          <div className="flex items-center gap-1.5 mb-1.5"><ArrowUpRight className="h-4 w-4 text-indigo-500" /><span className="text-[9px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">Top Project</span></div>
          <p className="text-sm font-bold text-slate-900 truncate" style={{ fontFamily: 'Manrope' }}>{topProject?.name || '--'}</p>
          <p className="text-[9px] text-slate-500 mt-1">{fmt(topProject?.value)} ({topProjectPct}%)</p>
        </div>
        <div className="kpi-card" data-testid="coll-projects">
          <div className="flex items-center gap-1.5 mb-1.5"><BarChart3 className="h-4 w-4 text-slate-500" /><span className="text-[9px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">Projects</span></div>
          <p className="text-lg sm:text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{byProject.length}</p>
          <p className="text-[9px] text-slate-500 mt-1">Avg: {fmt(avgPerProject)}</p>
        </div>
        <div className="kpi-card" data-testid="coll-concentration">
          <div className="flex items-center gap-1.5 mb-1.5"><Layers className="h-4 w-4 text-amber-500" /><span className="text-[9px] sm:text-xs font-semibold text-slate-400 uppercase tracking-wider whitespace-nowrap">Top 3 Share</span></div>
          <p className="text-lg sm:text-xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>{top3Pct}%</p>
          <p className="text-[9px] text-slate-500 mt-1">Concentration</p>
        </div>
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Collection by Project - Horizontal Bar */}
        <Card className="border-slate-200 shadow-sm lg:col-span-2" data-testid="coll-project-chart">
          <CardHeader className="pb-2 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><BarChart3 className="h-4 w-4 text-[#1E5FAD]" /><CardTitle className="text-sm font-semibold text-slate-900">Collection by Project</CardTitle></div>
              <span className="text-[10px] text-slate-400">{byProject.length} projects</span>
            </div>
          </CardHeader>
          <CardContent className="pt-3">
            {projectData.length > 0 ? (
              <div className="space-y-1.5">
                {projectData.map((p, i) => (
                  <div key={p.name} className="flex items-center gap-2 group" data-testid={`coll-project-row-${i}`}>
                    <span className="text-[10px] font-medium text-slate-600 w-28 sm:w-36 truncate flex-shrink-0">{p.name}</span>
                    <div className="flex-1 h-5 bg-slate-100 rounded-sm overflow-hidden relative">
                      <div className="h-full rounded-sm transition-all duration-500" style={{ width: `${Math.max(2, parseFloat(p.pct))}%`, background: COLORS[i % COLORS.length] }} />
                    </div>
                    <span className="text-[10px] font-semibold text-slate-700 w-16 text-right flex-shrink-0">{fmt(p.value)}</span>
                    <span className="text-[9px] text-slate-400 w-10 text-right flex-shrink-0">{p.pct}%</span>
                  </div>
                ))}
              </div>
            ) : <div className="h-48 flex items-center justify-center text-sm text-slate-400">No project data</div>}
          </CardContent>
        </Card>

        {/* Receipt Type Breakdown - Donut */}
        <Card className="border-slate-200 shadow-sm" data-testid="coll-type-chart">
          <CardHeader className="pb-2 border-b border-slate-100">
            <div className="flex items-center gap-2"><PieChart className="h-4 w-4 text-emerald-500" /><CardTitle className="text-sm font-semibold text-slate-900">By Receipt Type</CardTitle></div>
          </CardHeader>
          <CardContent className="pt-3">
            {byType.length > 0 ? (
              <div>
                <ResponsiveContainer width="100%" height={160}>
                  <RPieChart>
                    <Pie data={byType} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={65} paddingAngle={2}>
                      {byType.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                    </Pie>
                    <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '11px' }} formatter={v => fmt(v)} />
                  </RPieChart>
                </ResponsiveContainer>
                <div className="space-y-1.5 mt-2">
                  {byType.map((t, i) => (
                    <div key={t.name} className="flex items-center gap-2">
                      <div className="h-2.5 w-2.5 rounded-full flex-shrink-0" style={{ background: COLORS[i % COLORS.length] }} />
                      <span className="text-[10px] text-slate-600 truncate flex-1">{RECEIPT_ALIAS[t.name] || t.name}</span>
                      <span className="text-[10px] font-semibold text-slate-700">{fmt(t.value)}</span>
                    </div>
                  ))}
                </div>
              </div>
            ) : <div className="h-48 flex items-center justify-center text-sm text-slate-400">No type data</div>}
          </CardContent>
        </Card>
      </div>

      {/* Trend Chart */}
      <Card className="border-slate-200 shadow-sm" data-testid="coll-trend-chart">
        <CardHeader className="pb-2 border-b border-slate-100">
          <div className="flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-500" /><CardTitle className="text-sm font-semibold text-slate-900">Monthly Collection Trend</CardTitle></div>
        </CardHeader>
        <CardContent className="pt-3">
          {trend.length > 0 ? (
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={trend}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#64748B' }} />
                <YAxis tick={{ fontSize: 10, fill: '#64748B' }} tickFormatter={fmt} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: '1px solid #E2E8F0', fontSize: '11px' }} formatter={v => [fmt(v), "Collection"]} />
                <Area type="monotone" dataKey="value" fill="#10B98120" stroke="#10B981" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          ) : <div className="h-48 flex items-center justify-center text-sm text-slate-400">Trend data available after cashflow upload</div>}
        </CardContent>
      </Card>

      {/* Collection Target vs Actual by Project */}
      {byProjectTarget.length > 0 && (
        <Card className="border-slate-200 shadow-sm" data-testid="coll-target-table">
          <CardHeader className="pb-2 border-b border-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2"><Target className="h-4 w-4 text-blue-600" /><CardTitle className="text-sm font-semibold text-slate-900">Collection Target vs Actual — by Project</CardTitle></div>
              <span className="text-[10px] text-slate-400">{byProjectTarget.length} projects</span>
            </div>
          </CardHeader>
          <CardContent className="pt-3">
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-2 px-2 font-semibold text-slate-600">Project</th>
                    <th className="text-right py-2 px-2 font-semibold text-blue-600">Target</th>
                    <th className="text-right py-2 px-2 font-semibold text-emerald-600">Actual</th>
                    <th className="text-right py-2 px-2 font-semibold text-slate-600">Variance</th>
                    <th className="text-right py-2 px-2 font-semibold text-slate-600">Eff %</th>
                  </tr>
                </thead>
                <tbody>
                  {byProjectTarget.map((p, i) => {
                    const variance = p.actual - p.target;
                    return (
                      <tr key={i} className={`border-b border-slate-100 ${i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'}`}>
                        <td className="py-2 px-2 font-medium text-slate-800">{p.name}</td>
                        <td className="py-2 px-2 text-right text-blue-600">{p.target > 0 ? fmt(p.target) : '-'}</td>
                        <td className="py-2 px-2 text-right text-emerald-600 font-medium">{fmt(p.actual)}</td>
                        <td className={`py-2 px-2 text-right font-medium ${variance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>{p.target > 0 ? `${variance >= 0 ? '+' : ''}${fmt(variance)}` : '-'}</td>
                        <td className="py-2 px-2 text-right">
                          {p.target > 0 ? (
                            <span className={`inline-block px-1.5 py-0.5 rounded-full text-[10px] font-bold ${p.efficiency >= 100 ? 'bg-emerald-100 text-emerald-700' : p.efficiency >= 80 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>
                              {p.efficiency}%
                            </span>
                          ) : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="bg-[#0F1D32] text-white font-bold">
                    <td className="py-2 px-2">Total</td>
                    <td className="py-2 px-2 text-right">{fmt(collTarget)}</td>
                    <td className="py-2 px-2 text-right text-emerald-300">{fmt(collection)}</td>
                    <td className={`py-2 px-2 text-right ${collection >= collTarget ? 'text-emerald-300' : 'text-red-300'}`}>{collTarget > 0 ? `${collection >= collTarget ? '+' : ''}${fmt(collection - collTarget)}` : '-'}</td>
                    <td className="py-2 px-2 text-right"><span className="px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-white/20">{collEfficiencyVsTarget}%</span></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Insights Panel */}
      {hasData && (
        <Card className="border-slate-200 shadow-sm bg-gradient-to-br from-slate-50 to-blue-50/30" data-testid="coll-insights">
          <CardHeader className="pb-2 border-b border-slate-100"><CardTitle className="text-sm font-semibold text-slate-900">Key Insights</CardTitle></CardHeader>
          <CardContent className="pt-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {topProject && (
                <div className="flex items-start gap-2 p-2.5 bg-white rounded-lg border border-slate-100">
                  <ArrowUpRight className="h-4 w-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                  <div><p className="text-xs font-medium text-slate-800">Top Contributor: {topProject.name}</p><p className="text-[10px] text-slate-500 mt-0.5">Accounts for {topProjectPct}% of total collection ({fmt(topProject.value)})</p></div>
                </div>
              )}
              {collEfficiency && (
                <div className="flex items-start gap-2 p-2.5 bg-white rounded-lg border border-slate-100">
                  <TrendingUp className={`h-4 w-4 mt-0.5 flex-shrink-0 ${parseFloat(collEfficiency) >= 80 ? 'text-emerald-500' : parseFloat(collEfficiency) >= 50 ? 'text-amber-500' : 'text-red-500'}`} />
                  <div><p className="text-xs font-medium text-slate-800">Collection Efficiency: {collEfficiencyVsTarget}%</p><p className="text-[10px] text-slate-500 mt-0.5">Collected {fmt(collection)} against target of {fmt(collTarget)}</p></div>
                </div>
              )}
              {parseFloat(top3Pct) > 60 && (
                <div className="flex items-start gap-2 p-2.5 bg-white rounded-lg border border-slate-100">
                  <Layers className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
                  <div><p className="text-xs font-medium text-slate-800">High Concentration Risk</p><p className="text-[10px] text-slate-500 mt-0.5">Top 3 projects contribute {top3Pct}% of total collection</p></div>
                </div>
              )}
              {byType.length > 1 && (
                <div className="flex items-start gap-2 p-2.5 bg-white rounded-lg border border-slate-100">
                  <PieChart className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <div><p className="text-xs font-medium text-slate-800">{byType.length} Receipt Streams</p><p className="text-[10px] text-slate-500 mt-0.5">Primary: {RECEIPT_ALIAS[byType[0].name] || byType[0].name} ({fmt(byType[0].value)})</p></div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Manual Target Entry — Admin/Manager only */}
      <TargetManager token={token} targetType="collection" />
      </>
      )}
    </div>
  );
}
